import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';

export class StudioIntroScene extends Phaser.Scene {
  constructor() {
    super('StudioIntroScene');
    this.hasTransitioned = false;
  }

  preload() {
    // Only load the lightweight studio logo (60KB) so the intro starts immediately
    this.load.image('studio_logo', '/assets/ui/tinyplay_logo.png');
  }

  create() {
    this.hasTransitioned = false;
    const { width, height } = this.scale;

    // 1. Deep cinematic black background
    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    // Subtle dark indigo vignette
    const vignette = this.add.graphics();
    vignette.fillGradientStyle(0x020617, 0x020617, 0x000000, 0x000000, 0.7);
    vignette.fillRect(0, 0, width, height);
    vignette.setDepth(1);

    // 2. Ambient floating golden-cyan dust particles in darkness
    const particleGraphics = this.make.graphics({ x: 0, y: 0, add: false });
    particleGraphics.fillStyle(0xffffff, 1);
    particleGraphics.fillCircle(4, 4, 3);
    particleGraphics.generateTexture('intro_particle', 8, 8);
    particleGraphics.destroy();

    const particles = this.add.particles(0, 0, 'intro_particle', {
      x: { min: width * 0.15, max: width * 0.85 },
      y: { min: height * 0.2, max: height * 0.8 },
      lifespan: 2400,
      speedX: { min: -15, max: 15 },
      speedY: { min: -25, max: -5 },
      scale: { start: 0.8, end: 0.1 },
      alpha: { start: 0.6, end: 0 },
      tint: [0x38bdf8, 0xffd166, 0xffffff],
      quantity: 1,
      frequency: 180
    }).setDepth(3);

    // 3. Volumetric Backlight Glow behind Logo (Gameloft-style radial burst)
    const backGlow = this.add.graphics().setDepth(4);
    backGlow.fillStyle(0x0ea5e9, 0.25);
    backGlow.fillCircle(width / 2, height / 2 - 12, 140);
    backGlow.fillStyle(0x38bdf8, 0.4);
    backGlow.fillCircle(width / 2, height / 2 - 12, 90);
    backGlow.fillStyle(0xffffff, 0.8);
    backGlow.fillCircle(width / 2, height / 2 - 12, 25);
    backGlow.setAlpha(0);

    // 4. Logo Sprite (TINY PLAY GAME STUDIO)
    const logo = this.add.image(width / 2, height / 2 - 12, 'studio_logo')
      .setOrigin(0.5)
      .setScale(0.24)
      .setAlpha(0)
      .setDepth(10);

    // 5. "PRESENTS" Studio Subtitle
    const subtitle = this.add.text(width / 2, height / 2 + 105, 'P R E S E N T S', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '13px',
      fontWeight: 'bold',
      letterSpacing: 8,
      color: '#94a3b8'
    }).setOrigin(0.5).setAlpha(0).setDepth(11);

    // 6. Gameloft-style Diagonal Lens Sheen / Light Beam Sweep
    const lightBeam = this.add.graphics().setDepth(15);
    lightBeam.fillStyle(0xffffff, 0.85);
    lightBeam.fillTriangle(0, 0, 40, 0, 15, 260);
    lightBeam.fillTriangle(40, 0, 55, 0, 30, 260);
    lightBeam.setAngle(-22);
    lightBeam.setPosition(width / 2 - 240, height / 2 - 140);
    lightBeam.setAlpha(0);

    // 7. Unobtrusive Skip Button on bottom right
    const skipBtn = this.add.text(width - 24, height - 20, 'SKIP ⏭', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '11px',
      color: '#64748b',
      backgroundColor: 'rgba(0,0,0,0.6)',
      padding: { x: 8, y: 4 }
    }).setOrigin(1, 1).setAlpha(0.65).setDepth(20).setInteractive({ useHandCursor: true });

    skipBtn.on('pointerdown', () => this.transitionToNext());
    this.input.on('pointerdown', () => this.transitionToNext());
    this.input.keyboard.on('keydown-SPACE', () => this.transitionToNext());
    this.input.keyboard.on('keydown-ENTER', () => this.transitionToNext());

    // -----------------------------------------------------------
    // CINEMATIC TIMELINE SEQUENCE
    // -----------------------------------------------------------

    // Step A: Logo zooms gently forward from darkness
    this.tweens.add({
      targets: logo,
      alpha: 1,
      scale: 0.32,
      duration: 1100,
      ease: 'Cubic.easeOut',
      onStart: () => {
        try {
          audio.playBeep(220, 'triangle', 0.8, 0.15);
          setTimeout(() => audio.playBeep(440, 'sine', 0.6, 0.12), 180);
        } catch (_) {}
      }
    });

    // Step B: Backlight blooms
    this.tweens.add({
      targets: backGlow,
      alpha: 1,
      scaleX: 1.25,
      scaleY: 1.25,
      duration: 1200,
      ease: 'Quad.easeOut'
    });

    // Step C: Subtitle appears with soft glow
    this.tweens.add({
      targets: subtitle,
      alpha: 0.9,
      duration: 800,
      delay: 700,
      ease: 'Linear'
    });

    // Step D: The Iconic Diagonal Light Gleam Sweep across the logo
    this.time.delayedCall(1100, () => {
      if (this.hasTransitioned) return;
      lightBeam.setAlpha(0.9);
      try {
        audio.playBeep(880, 'sine', 0.25, 0.18);
        setTimeout(() => audio.playBeep(1320, 'sine', 0.35, 0.15), 60);
      } catch (_) {}

      this.tweens.add({
        targets: lightBeam,
        x: width / 2 + 240,
        alpha: 0,
        duration: 750,
        ease: 'Cubic.easeOut'
      });
    });

    // Step E: Subtle Camera slow-zoom for depth
    this.cameras.main.zoomTo(1.04, 3000, 'Linear');

    // Step F: Automatic transition after full presentation
    this.time.delayedCall(2700, () => {
      this.transitionToNext();
    });
  }

  transitionToNext() {
    if (this.hasTransitioned) return;
    this.hasTransitioned = true;

    sessionStorage.setItem('studio_intro_seen', '1');

    this.cameras.main.fade(500, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('BootScene');
    });
  }
}
